import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import parse from 'html-react-parser';
import { IconWrapper } from '@flexo/general';
import { ALERT } from '@flexo/reducers';
import { _navigate } from '../../../../../resolvers/src/lib/helpers';
import styles from './../alert.module.scss';

export type BannerType = 'success' | 'error';

export interface UnifiedBannerProps {
  type: BannerType;
  messageKey: string;
  id?: string;
  onCustomAction?: {
    text: string;
    action: () => void;
  };
  navigationAction?: {
    text: string;
    path: string;
  };
}

// Configuration for banner types
const BANNER_CONFIG = {
  success: {
    containerClass: styles.Banner__Password__Reset__Success,
    titleClass: styles.Banner__Title__Positive,
    iconName: 'check-encircled_outline',
    iconStyle: { height: 16, width: 16, marginRight: 10, marginTop: -2 },
    showCloseButton: true
  },
  error: {
    containerClass: styles.Banner__Unexpected__Error,
    titleClass: styles.Banner__Title__Negative,
    iconName: 'alert',
    iconStyle: {},
    showCloseButton: true
  }
} as const;

export default function UnifiedBanner({ 
  type, 
  messageKey, 
  id,
  onCustomAction,
  navigationAction 
}: UnifiedBannerProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const config = BANNER_CONFIG[type];
  const alertContent = t(messageKey, { returnObjects: true }) as any;
  
  const handleNavigation = (path: string) => {
    dispatch(ALERT.ResetAlert({} as any));
    _navigate(path);
  };

  const handleClose = () => {
    dispatch(ALERT.ResetAlert({} as any));
  };

  return (
    <div id={id} className={`${config.containerClass} ${styles.Banner}`}>
      <div className={config.titleClass}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <IconWrapper iconName={config.iconName} style={config.iconStyle} />
          {alertContent.title}
        </div>
        {config.showCloseButton && (
          <div onClick={handleClose}>
            <IconWrapper
              className={styles.Banner__Title__Icon__Close}
              iconName="cross"
            />
          </div>
        )}
      </div>
      <div className={`paragraph ${styles.Banner__Content}`}>
        {typeof alertContent.description === 'string' 
          ? parse(alertContent.description) 
          : (alertContent.description || alertContent.message)}
        {config.showCloseButton && (onCustomAction || navigationAction) && (
          <>
            {' '}
            {onCustomAction && (
              <span
                className={styles.Banner__Content__Link}
                onClick={() => onCustomAction.action()}
              >
                {onCustomAction.text}
              </span>
            )}
            {navigationAction && (
              <span
                className={styles.Banner__Content__Link}
                onClick={() => handleNavigation(navigationAction.path)}
              >
                {navigationAction.text}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
} 